import { useEffect, useState, type FormEvent } from 'react';
import { actualizarUsuario, crearUsuario } from '../../lib';
import type { UserWithProfile } from '../../lib';
import type { RolUsuario } from '../../lib';
import { getSucursales } from '../../lib/storage';
import type { Sucursal } from '../../lib/appTypes';

interface Props {
  initialUser?: UserWithProfile | null;
  onSaved: (user: UserWithProfile) => void;
  onCancel: () => void;
}

const ROLES = [
  { value: 'CONDUCTOR', label: 'Conductor' },
  { value: 'VENDEDOR', label: 'Vendedor' },
] as const;

export default function FormUsuario({ initialUser, onSaved, onCancel }: Props) {
  const initialRole = (initialUser?.profile?.rol ?? 'CONDUCTOR') as RolUsuario;
  const [form, setForm] = useState({
    username: initialUser?.username ?? '',
    password: '',
    email: initialUser?.email ?? '',
    identificacion: initialUser?.profile?.identificacion ?? '',
    razonSocial: initialUser?.profile?.razon_social ?? '',
    rol: initialRole,
    sucursal: initialUser?.profile?.sucursal ? String(initialUser.profile.sucursal) : '',
  });
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isEditing = Boolean(initialUser?.id);
  const roleOptions =
    initialRole === 'CONDUCTOR' || initialRole === 'VENDEDOR'
      ? ROLES
      : [{ value: initialRole, label: initialRole }, ...ROLES];

  function handle(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  useEffect(() => {
    let cancelled = false;

    void getSucursales()
      .then(response => {
        if (!cancelled) {
          setSucursales(response);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('No se pudieron cargar las sucursales.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditing && initialUser?.id) {
        await actualizarUsuario(initialUser.id, {
          username: form.username.trim(),
          email: form.email.trim(),
          identificacion: form.identificacion.trim(),
          razon_social: form.razonSocial.trim(),
          rol: form.rol,
          sucursal: form.sucursal ? Number(form.sucursal) : null,
        });
      } else {
        await crearUsuario({
          username: form.username.trim(),
          password: form.password,
          email: form.email.trim(),
          identificacion: form.identificacion.trim(),
          razon_social: form.razonSocial.trim(),
          rol: form.rol,
          sucursal: form.sucursal ? Number(form.sucursal) : null,
        });
      }

      onSaved({
        id: initialUser?.id,
        username: form.username.trim(),
        email: form.email.trim(),
        profile: {
          razon_social: form.razonSocial.trim(),
          identificacion: form.identificacion.trim(),
          rol: form.rol,
          sucursal: form.sucursal ? Number(form.sucursal) : null,
          estado: initialUser?.profile?.estado ?? true,
        },
      });
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? `No se pudo ${isEditing ? 'actualizar' : 'crear'} el usuario.`);
    } finally {
      setLoading(false);
    }
  }

  const inputCls =
    'w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-stone-900">{isEditing ? 'Editar usuario' : 'Crear usuario'}</h3>
        <p className="text-sm text-stone-500">
          Define el acceso base y asigna el rol operativo que se usará para personal y autos.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Usuario</label>
          <input name="username" value={form.username} onChange={handle} required className={inputCls} autoComplete="off" />
        </div>
        <div>
          <label className={labelCls}>Correo</label>
          <input name="email" type="email" value={form.email} onChange={handle} required className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Contraseña</label>
          <input name="password" type="password" value={form.password} onChange={handle} required={!isEditing} minLength={8} className={inputCls} autoComplete="new-password" disabled={isEditing} />
        </div>
        <div>
          <label className={labelCls}>Rol</label>
          <select name="rol" value={form.rol} onChange={handle} className={inputCls}>
            {roleOptions.map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Identificación</label>
          <input name="identificacion" value={form.identificacion} onChange={handle} required className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Nombre visible</label>
          <input name="razonSocial" value={form.razonSocial} onChange={handle} required className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Sucursal</label>
          <select name="sucursal" value={form.sucursal} onChange={handle} className={inputCls}>
            <option value="">Sin sucursal</option>
            {sucursales.map(sucursal => (
              <option key={sucursal.id} value={sucursal.id}>
                {sucursal.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="ui-btn ui-btn-secondary"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="ui-btn ui-btn-dark"
        >
          {loading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear usuario'}
        </button>
      </div>
    </form>
  );
}
